import { domains } from 'config/env'
import { Logger } from 'logging'
import { createApiClient } from 'lib/Api'

const baseApiAddress = `${domains.SECONDSTEP_API}/`
const sitesBase = '/sites'

const logEvent = ({ method, url, data, error }) => {
  const eventProperties = { method }

  if (url) {
    eventProperties.url = url
  }
  if (data) {
    eventProperties.data = data
  }
  if (error) {
    eventProperties.error = error
  }

  Logger.logEvent({
    name: 'Second Step API Error',
    properties: eventProperties,
  })
}

export const createClient = customAddress => {
  const baseURL = customAddress || baseApiAddress
  const _api = createApiClient(baseURL)

  return {
    baseApiAddress: customAddress || baseApiAddress,
    getCountries: () => {
      const url = '/location/countries'
      return _api.get(url)
    },

    getRegions: country => {
      const url = `/location/regions?country=${country}`
      return _api.get(url)
    },

    getCitiesWithRegion: (country, region) => {
      const url = `location/cities?country=${country}&region=${region}`
      return _api.get(url)
    },

    getCitiesWithoutRegion: country => {
      const url = `location/cities?country=${country}`
      return _api.get(url)
    },

    getNSCustomersWithRegion: (city, country, region) => {
      const url = `/netsuitecustomers?country=${country}&region=${region}&city=${city}`
      return _api.get(url)
    },

    getNSCustomersWithoutRegion: (city, country) => {
      const url = `netsuitecustomers?country=${country}&city=${city}`
      return _api.get(url)
    },

    getUserProfile: () => {
      const url = '/profile/me'
      return _api.get(url)
    },

    getUserAccess: () => {
      const url = '/access/me'
      return _api.get(url)
    },

    getLicenses: () => {
      const url = `/licenses/me?includeFutureLicenses=true`
      return _api.get(url)
    },

    postLicenseAssignment: licenseAssignment => {
      const url = '/licenses'
      return _api.post(url, licenseAssignment)
    },

    getLicense: licenseId => {
      const url = `/license?LicenseId=${licenseId}`
      return _api.get(url)
    },

    postLicenseUsers: payload => {
      const url = '/licenseuserrole/bulk'
      return _api.post(url, payload)
    },

    postLicenseUser: payload => {
      const url = '/licenseuserrole'
      return _api.post(url, payload)
    },

    putLicenseUser: payload => {
      const url = '/licenseuserrole'
      return _api.put(url, payload)
    },

    deleteLicenseUser: payload => {
      const url = '/licenseuserrole'
      return _api.delete(url, { data: payload })
    },

    remindLicenseUser: payload => {
      const url = '/licenseuserrole/remind'
      return _api.post(url, payload)
    },

    remindAllLicenseUsers: payload => {
      const url = '/licenseuserrole/remindAll'
      return _api.post(url, payload)
    },

    getSites: async () => {
      const url = `${sitesBase}/me?includeFutureLicenses=true`
      try {
        return await _api.get(url)
      } catch (error) {
        logEvent({ method: 'GET', url, error: error.message })
        throw error
      }
    },

    getSiteUsers: async siteId => {
      const url = `${sitesBase}/${siteId}/siteusers`
      try {
        return await _api.get(url)
      } catch (error) {
        logEvent({ method: 'GET', url, data: { siteId }, error: error.message })
        throw error
      }
    },

    createSiteUser: async (siteUser, sendEmail) => {
      const url = `${sitesBase}/${siteUser.siteId}/siteusers?sendEmail=${sendEmail}`
      try {
        return await _api.post(url, siteUser)
      } catch (error) {
        logEvent({ method: 'POST', url, data: siteUser, error: error.message })
        throw error
      }
    },

    createSiteUsers: (siteId, siteUsers, sendEmail) => {
      const url = `${sitesBase}/${siteId}/siteusers?sendEmail=${sendEmail}`
      return _api.patch(url, siteUsers)
    },

    updateSiteUser: siteUser => {
      const url = `${sitesBase}/${siteUser.siteId}/siteusers/${siteUser.id}`
      return _api.put(url, siteUser)
    },

    remindSiteUser: siteUser => {
      const url = `${sitesBase}/${siteUser.siteId}/siteusers/${siteUser.id}/remind`
      return _api.post(url)
    },

    remindSiteUsers: (siteId, emailFilters) => {
      const url = `${sitesBase}/${siteId}/siteusers/remind`
      return _api.post(url, emailFilters)
    },
  }
}

const api = createClient()
export default api
